import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import {
  CONSENT_STATUS,
  type ConsentStatus,
  consentChecking,
  consentDenied,
  consentError,
  consentGranted,
  consentReset,
  consentSubmitting,
  type StateWithSidekickConsent,
  selectConsentStatus,
} from '../state/aiSidekickConsentSlice';
import { closePanel, openPanel } from '../state/aiSidekickSlice';
import { useConsentApi } from './useConsentApi';

/** Options accepted by {@link SidekickConsentGate.requestOpen}. */
export type RequestOpenOptions = {
  /**
   * Runs only when the status check resolves to *already consented* and
   * the panel is opened. The entry card uses this to replay a chip's
   * suggestion submission for a returning (consented) user, while a
   * first-time user just sees the consent modal with nothing sent.
   */
  onGranted?: () => void;
  /**
   * Whether a "not consented" answer opens the drawer to the consent
   * modal. Defaults to `true`. The dock's bar sets it to `false` so a
   * first-time user lands on the welcome card instead; the status is
   * still recorded, so the card's own entry raises the modal later.
   */
  openOnDenied?: boolean;
};

/**
 * Outcome of a {@link SidekickConsentGate.requestOpen} call. Returned
 * (rather than toasted here) so the calling UI component owns error
 * presentation — the package convention is that components fire toasts,
 * hooks stay presentation-free. `'pending'` means a check or an accept
 * was already in flight so the call was a no-op.
 */
export type RequestOpenOutcome = 'granted' | 'denied' | 'error' | 'pending';

/** Imperative surface returned by {@link useSidekickConsentGate}. */
export type SidekickConsentGate = {
  /** Current consent-check status for this session. */
  status: ConsentStatus;
  /**
   * Entry-point handler: checks consent status, then opens the panel to
   * the chat (`'granted'`), opens it to the consent modal (`'denied'`),
   * or leaves the panel closed (`'error'`). Returns the outcome so the
   * caller can present the error toast on `'error'`.
   */
  requestOpen: (options?: RequestOpenOptions) => Promise<RequestOpenOutcome>;
  /**
   * Records acceptance (Agree). Resolves `true` on success and `false`
   * on failure (status reverts to `denied` so the modal stays open); the
   * caller presents the error toast on `false`.
   */
  agree: () => Promise<boolean>;
  /** Declines (Disagree / X): collapses the panel and re-arms the gate. */
  decline: () => void;
};

/**
 * Orchestrates the Sidekick legal-disclaimer consent gate. Composes the
 * resolved consent API ({@link useConsentApi}), the consent slice, and
 * the panel open/close actions into the three imperative handlers the
 * entry card and consent modal wire to.
 *
 * Presentation-free by design: a failed status lookup / acceptance
 * resolves to `'error'` / `false` and the calling component surfaces the
 * shared error toast — matching the package convention where
 * `MessageFeedbackRow` (component) fires the toast, not its submission
 * hook.
 *
 * A grant served from the browser cache is trusted in full: the drawer
 * opens and the chat can send at once. The cache is then checked
 * against the backend in the background, and if the backend reports the
 * user as no longer consented — new legal terms, or a revoked decision —
 * the gate re-arms over the open drawer. Turns already sent stay sent;
 * the modal only stops the next one.
 *
 * An answer the browser holds (the cache, or the localStorage fallback)
 * settles synchronously and never enters `checking`, so entry surfaces
 * show their loading state only while a backend lookup is in flight.
 */
export const useSidekickConsentGate = (): SidekickConsentGate => {
  const dispatch = useDispatch();
  const status = useSelector(selectConsentStatus);
  const {
    peekConsentStatus,
    getConsentStatus,
    acceptConsent,
    revalidateConsent,
  } = useConsentApi();
  // The check is detached, so `status` from this render is stale by the
  // time it settles. A ref synced in an effect is not enough either: a
  // check resolving in a microtask beats the effect.
  const store = useStore<Partial<StateWithSidekickConsent>>();

  const revalidateCachedGrant = useCallback(() => {
    void revalidateConsent().then((stillConsented) => {
      // Only a positive "no longer consented" re-arms the gate, and only
      // while the session still holds the grant this check was for.
      if (
        stillConsented === false &&
        selectConsentStatus(store.getState()) === CONSENT_STATUS.GRANTED
      ) {
        dispatch(consentDenied());
      }
    });
  }, [dispatch, revalidateConsent, store]);

  const requestOpen = useCallback(
    async (options?: RequestOpenOptions): Promise<RequestOpenOutcome> => {
      const openOnDenied = options?.openOnDenied ?? true;
      // Guard against re-entry from a repeated entry click. Only run a
      // fresh status check from `idle` (first entry) or `error` (retry).
      // Re-checking while already denied/checking/submitting could let a
      // late `consentDenied()` clobber a concurrent `agree()` and
      // re-block the chat.
      if (status === CONSENT_STATUS.GRANTED) {
        // Already consented this session — just (re)open, no re-check.
        dispatch(openPanel());
        options?.onGranted?.();
        return 'granted';
      }
      if (status === CONSENT_STATUS.DENIED) {
        // Gate is already armed; show it without re-checking.
        if (openOnDenied) {
          dispatch(openPanel());
        }
        return 'denied';
      }
      if (
        status === CONSENT_STATUS.CHECKING ||
        status === CONSENT_STATUS.SUBMITTING
      ) {
        return 'pending';
      }
      try {
        // `checking` drives the loading states, so it is only entered
        // when the answer has to come from the backend.
        let consent = peekConsentStatus();
        if (!consent) {
          dispatch(consentChecking());
          consent = await getConsentStatus();
        }
        if (consent.hasConsented) {
          dispatch(consentGranted());
          dispatch(openPanel());
          // A no-op unless the answer came from the cache.
          revalidateCachedGrant();
          options?.onGranted?.();
          return 'granted';
        }
        dispatch(consentDenied());
        if (openOnDenied) {
          dispatch(openPanel());
        }
        return 'denied';
      } catch {
        // Status lookup failed — collapse the panel (matches the Figma
        // failure flow: drawer closed + toast). Closing it explicitly
        // covers the case where the panel was already open (e.g. a
        // re-check from an entry click while the gate is showing), so a
        // failure can never leave the chat exposed. The caller presents
        // the error toast.
        dispatch(consentError());
        dispatch(closePanel());
        return 'error';
      }
    },
    [
      dispatch,
      getConsentStatus,
      peekConsentStatus,
      revalidateCachedGrant,
      status,
    ]
  );

  const agree = useCallback(async (): Promise<boolean> => {
    dispatch(consentSubmitting());
    try {
      await acceptConsent();
      dispatch(consentGranted());
      return true;
    } catch {
      // Keep the modal open (revert to denied) so the user can retry;
      // the caller presents the error toast.
      dispatch(consentDenied());
      return false;
    }
  }, [dispatch, acceptConsent]);

  const decline = useCallback(() => {
    // Collapse the drawer and re-arm the gate so the modal re-appears on
    // every subsequent entry until the user consents.
    dispatch(closePanel());
    dispatch(consentReset());
  }, [dispatch]);

  return { status, requestOpen, agree, decline };
};
